import {Util} from './util'

export type FormErrors<TFields> = Partial<Record<keyof TFields, Array<string>>>

export interface FormState<TFields> {
  data: TFields
  errors: FormErrors<TFields>
}

export interface FieldError {
  field: string
  errors: string[]
}

type Transformers<TFields> = {
  [P in keyof TFields]?: (x: TFields[P]) => TFields[P]
}

export class FormUtil {
  /**
   * Initializes a form state with provided initial state
   *
   * @example
   * interface FormFields {
   *   field1: string
   *   field2: number
   * }
   * const form: FormState<FormFields> =
   *   FormUtil.initForm<FormFields>({
   *     field1: 'abc',
   *     field2: 123
   *   })
   */
  static initForm<TFields>(initialState: TFields): FormState<TFields> {
    return {
      data: Util.clone(initialState),
      errors: {} as FormErrors<TFields>
    }
  }

  /**
   * Updates form state based on the provided delta
   *
   * @example
   * interface FormFields {
   *   field1: string
   *   field2: number
   * }
   * const form =
   *   FormUtil.initForm<FormFields>({
   *     field1: 'abc',
   *     field2: 123
   *   })
   * const newForm =
   *   FormUtil.updateForm(form, {
   *     field1: 'abc'
   *   })
   */
  static updateForm<TFields>(
    form: FormState<TFields>,
    delta: Partial<TFields>
  ): FormState<TFields> {
    const newForm = Util.clone(form)

    for (const field of Object.keys(delta) as (keyof TFields)[]) {
      const value = delta[field]
      if (form.data[field] !== value && value !== undefined) {
        delete newForm.errors[field]
        newForm.data[field] = value! // eslint-disable-line @typescript-eslint/no-non-null-assertion
      }
    }

    return newForm
  }

  /**
   * Updates form field errors based on the provided error list
   *
   * @example
   * interface FormFields {
   *   field1: string
   *   field2: number
   * }
   * const form =
   *   initForm<FormFields>({
   *     field1: 'abc',
   *     field2: 123
   *   })
   * const fieldErrors: FieldError[] = [
   *   {
   *     field: 'field1',
   *     errors: ['error1', 'error2']
   *   }
   * ]
   * const newForm = applyErrors(form, fieldErrors)
   */
  static applyErrors<TFields extends object>(
    form: FormState<TFields>,
    fieldErrors: FieldError[]
  ): FormState<TFields> {
    const newForm = Util.clone(form)

    for (const {field, errors} of fieldErrors) {
      if (field in newForm.data) {
        newForm.errors[field as keyof TFields] = [...errors]
      }
    }

    return newForm
  }

  /**
   * Extracts form data from form state potentially transforming it
   */
  static extractData<TFields>(
    form: FormState<TFields>,
    transformers?: Transformers<TFields>
  ): TFields {
    const data = Util.clone(form.data)

    if (!transformers) {
      return data
    }

    for (const field of Object.keys(transformers) as (keyof TFields)[]) {
      const fn = transformers[field]
      const value = data[field]
      data[field] = fn ? fn(value) : value
    }

    return data
  }
}
